<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：営業所割当マスタ画面
 *
 * 作成者：2021/11/25 ノア）金
 * 最終修正者：2022/1/28 ノア）黄
 *
 -->
<template>
  <div class="tti-main-view">
    <!-- ヘッダー -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <el-button class="backButton" @click="onBackButtonClick" v-tooltip="getTooltip(tooltipType.back)">
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
          </el-button>
          <div class="tti-viewTitle">
            <tti-svg-icon icon-name="wo_008"></tti-svg-icon>
            <span>{{ $t('master.salesOfficeAllocationMaster.viewTitle') }}</span>
          </div>
        </div>
      </nav>
    </section>
    <!-- テーブル -->
    <section class="tti-padding-content">
      <div class="tti-lr-container">
        <!-- 営業所階層一覧 -->
        <div class="tti-tree left">
          <div class="tti-content-title">
            <span>{{ $t('master.salesOfficeAllocationMaster.salesOfficeAllocationList') }}</span>
          </div>
          <div class="content">
            <officeAllocDraggableTree
              :disabled="pageLock"
              :data="masterTree"
              :type="officeAllocType.BUSINESS_OFFICE"
              @treeUpdate="onTreeUpdate"
              :unallocDragConfirmCallback="unallocDragConfirmCallback"
            ></officeAllocDraggableTree>
          </div>
        </div>
        <!-- 未割当営業所一覧 -->
        <div class="tti-tree right">
          <div class="tti-content-title">
            <span>{{ $t('master.salesOfficeAllocationMaster.salesOfficeList') }}</span>
          </div>
          <div class="content">
            <div @click="showTarget = !showTarget">
              <span class="expander">
                <span v-if="showTarget">
                  <tti-svg-icon icon-name="su_021"></tti-svg-icon>
                </span>
                <span v-else>
                  <tti-svg-icon icon-name="su_022"></tti-svg-icon>
                </span>
              </span>
              <span>{{ $t('master.salesOfficeAllocationMaster.unAllocation') }}</span>
            </div>
            <div class="customTree">
              <officeAllocDraggableTree
                v-show="showTarget"
                :disabled="pageLock"
                :data="unAllocationMasterData"
                :type="officeAllocType.UN_ALLOC"
              ></officeAllocDraggableTree>
              <div class="tree-empty-text" v-if="!unAllocationMasterData || unAllocationMasterData.length === 0">
                {{ $t('messages.common.emptyTableData') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <!-- フッター -->
    <section class="tti-master-bottom-area">
      <div class="tti-bottom-left-area">
        <!-- 取消 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onCancelButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.cancel)"
        >
          <tti-svg-icon icon-name="su_005"></tti-svg-icon>
          {{ $t('buttons.cancel') }}
        </el-button>
      </div>
      <div class="tti-bottom-right-area">
        <!-- 保存 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onSaveButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.save)"
        >
          <tti-svg-icon icon-name="su_002"></tti-svg-icon>
          {{ $t('buttons.save') }}
        </el-button>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { Component } from 'vue-property-decorator'
import { DropContext } from 'vuedraggable'
import { cloneDeep, findIndex } from 'lodash'

import MST_BASE from '@/views/master/MST_BASE.vue'
import OfficeAllocDraggableTree from './OfficeAllocDraggableTree.vue'
import { i18n } from '@/locales'
import { common } from '@/store/modules'
import { getSalesOfficeAllocationData, updateSalesOfficeAllocationMasterData } from '@/store/apis'
import { TreeData, OfficeMasterData, MasterUpdateParam, AllocationOfficeItem, ResponseData } from '@/models'
import { OfficeAllocType } from '@/commons/constants'
import { msgBoxService } from '@/commons/services/msgBoxService'
import { localService } from '@/commons/services/localService'
import { AxiosResponse } from 'axios'
import { changeBlobToJson } from '@/store/apis/apiClient'

/**
 * 営業所割当マスタ画面
 *
 * @class
 */
@Component({
  name: 'mst-sales-ofc-alloc',
  components: { OfficeAllocDraggableTree }
})
export default class MST_SALES_OFC_ALLOC extends MST_BASE {
  /**
   * 画面ID
   */
  pageId = 'MST_SALES_OFC_ALLOC'

  /**
   * 画面名
   */
  pageName = i18n.t('master.salesOfficeAllocationMaster.viewTitle').toString()

  /**
   * 管理対象割当リスト
   */
  managementTargetAllocData: OfficeMasterData[] = []

  /**
   * エリアマスタTree
   */
  masterTree: TreeData[] = []

  /**
   * 未割当Tree
   */
  unAllocationMasterData: TreeData[] = []

  treeDataCache = {} as Record<string, TreeData[]>

  showTarget = true

  /**
   * 営業所割当種類
   */
  officeAllocType = OfficeAllocType

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  mounted(): void {
    this.$log.debug('MST_SALES_OFC_ALLOC > mounted')
    this.fetchTableData()
    this.checkPageLock()
  }

  /**
   * テーブルデータを検索して、初期化を実行する。
   *
   * @returns {Promise<boolean>} 検索実行結果
   */
  async fetchTableData(): Promise<boolean> {
    try {
      this.$log.debug('MST_SALES_OFC_ALLOC > fetchTableData')
      const data = (await getSalesOfficeAllocationData()) as OfficeMasterData
      this.initAllocData(data)
      this.fetchTableDataAfter()
      return true
    } catch (e) {
      this.$log.debug('MST_SALES_OFC_ALLOC > fetchTableData > error', e)
      throw e
    }
  }

  /**
   * テーブルデータの初期化を実行する。
   *
   * @param {OfficeMasterData} data 検索されたデータ
   * @returns {void}
   */
  initAllocData(data: OfficeMasterData): void {
    if (!data) {
      data = {} as OfficeMasterData
    }
    this.oldData = cloneDeep(data)
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.tableData = data
  }

  fetchTableDataAfter(): void {
    this.multipleSelection = []
    this.changeBusinessOfficeAllocDataToTree()
  }

  initTreeCache(): void {
    this.treeDataCache = {
      masterTree: cloneDeep(this.masterTree),
      unAllocationMasterData: cloneDeep(this.unAllocationMasterData)
    }
  }

  rollbackToTreeCache(): void {
    this.masterTree = cloneDeep(this.treeDataCache.masterTree)
    this.unAllocationMasterData = cloneDeep(this.treeDataCache.unAllocationMasterData)
  }

  /**
   * エリアマスタをTreeDataに変換する
   *
   * @returns {void}
   */
  // eslint-disable-next-line max-lines-per-function
  changeBusinessOfficeAllocDataToTree(): void {
    this.$log.debug('MST_SALES_OFC_ALLOC > changeAreaMasterToTree')

    const fillterOfficeData = (this.tableData as unknown as OfficeMasterData).businessOfficeList.map(
      ({ businessOfficeId, businessOfficeName, businessOfficeCode, parentId, hasManagementTarget }) => {
        return {
          id: businessOfficeId,
          parentId,
          label: `${businessOfficeName}(${businessOfficeCode})`,
          children: [],
          disabled: false,
          canDragToUnalloc: !hasManagementTarget && false,
          expand: true,
          isLastNode: false,
          type: OfficeAllocType.BUSINESS_OFFICE
        } as TreeData
      }
    )

    const map: Record<string, TreeData> = {}
    const masterTree = [] as TreeData[]

    fillterOfficeData.forEach(item => {
      if (item.id) {
        map[item.id] = item
      }
    })

    fillterOfficeData.forEach(item => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const parent = map[item.parentId]
      if (parent) {
        ;(parent.children || (parent.children = [])).push(item)
      } else {
        if (item.id === localService.userInfo?.businessOfficeId) {
          item.level = '0'
          item.disabled = true
          masterTree.push(item)
        }
      }
    })

    this.masterTree = masterTree

    this.unAllocationMasterData = (this.tableData as unknown as OfficeMasterData).unAllocationList.map(item => {
      return {
        id: item.businessOfficeId,
        label: `${item.businessOfficeName}(${item.businessOfficeCode})`,
        disabled: false,
        isLastNode: true,
        type: OfficeAllocType.UN_ALLOC,
        children: []
      }
    })

    this.initTreeCache()
  }

  /**
   * 営業所階層一覧から未割当営業所一覧へ、ドラッグした営業所の管理対象有無がtrueの場合。
   *
   * @returns {void}
   */
  unallocDragConfirmCallback(): void {
    msgBoxService.info(i18n.t('master.salesOfficeAllocationMaster.dragToUnallocWarning').toString())
  }

  /**
   * 取消ボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  onCancelButtonClick(): void {
    this.$log.debug('MST_SALES_OFC_ALLOC > onCancelButtonClick')

    if (!this.checkDataChanged()) {
      return
    }

    const confirmCallback = () => {
      this.tableData = cloneDeep(this.oldData)
      this.changeBusinessOfficeAllocDataToTree()
    }

    msgBoxService.confirm(i18n.t('messages.masterCommon.cancelConfirm').toString(), confirmCallback)
  }

  /**
   * 変更されましたデータを算出する。
   *
   * @returns {any} 変更されたデータ
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getChangedData(): any {
    const currentData = [] as AllocationOfficeItem[]
    this.changeTreeToList(currentData, this.masterTree[0])

    const updateDataList: AllocationOfficeItem[] = []

    currentData.forEach(item => {
      const businessOfficeId = item.businessOfficeId
      const parentId = item.parentId

      const index = findIndex(
        (this.tableData as unknown as OfficeMasterData).businessOfficeList,
        item => item.businessOfficeId === businessOfficeId && item.parentId === parentId
      )

      if (index === -1) {
        updateDataList.push(item)
      }
    })

    const unAllocationList: string[] = []

    this.unAllocationMasterData.forEach(item => {
      const businessOfficeId = item.id
      const index = (this.tableData as unknown as OfficeMasterData).unAllocationList.findIndex(
        unAllocation => unAllocation.businessOfficeId === businessOfficeId
      )
      if (index === -1) {
        unAllocationList.push(businessOfficeId)
      }
    })

    return {
      updateDataList,
      unAllocationList
    }
  }

  changeTreeToList(finallList: AllocationOfficeItem[], target: TreeData): void {
    const children = target.children

    children?.forEach(item => {
      const office = {
        businessOfficeId: item.id,
        parentId: target.id
      } as AllocationOfficeItem

      finallList.push(office)

      if (item.children) {
        this.changeTreeToList(finallList, item)
      }
    })
  }

  /**
   * データが変更されましたかをチェックする。
   *
   * @returns {boolean} 変更されているかどうか
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  checkDataChanged(): boolean {
    const { updateDataList, unAllocationList } = this.getChangedData()

    if ((!updateDataList || updateDataList.length === 0) && (!unAllocationList || unAllocationList.length === 0)) {
      return false
    }

    return true
  }

  /**
   * 保存ボタンをクリックした時の処理
   *
   * @returns {void}
   */
  async onSaveButtonClick(): Promise<void> {
    this.$log.debug('MST_SALES_OFC_ALLOC > onSaveButtonClick')
    await this.saveTableData()
  }

  /**
   * 保存ボタンをクリックした時の処理
   *
   * @returns {Promise<boolean>} 保存結果
   */
  async saveTableData(): Promise<boolean> {
    try {
      this.$log.debug('MST_SALES_OFC_ALLOC > saveTableData')

      const { updateDataList, unAllocationList } = this.getChangedData()

      if ((!updateDataList || updateDataList.length === 0) && (!unAllocationList || unAllocationList.length === 0)) {
        msgBoxService.info(i18n.t('messages.masterCommon.dataNotChanged').toString())
        return false
      }

      const params = {
        updateDataList,
        unAllocationList
      } as MasterUpdateParam

      await updateSalesOfficeAllocationMasterData(params)
      this.saveTableDataAfter()
      common.makeBusinessOfficeChange()

      return true
    } catch (e) {
      this.$log.debug('MST_SALES_OFC_ALLOC > saveTableData > error', e)
      const err = e as AxiosResponse<ResponseData>
      changeBlobToJson(err).then(commonResponse => {
        if (commonResponse.common.code === 'E00093') {
          this.fetchTableData()
        }
      })
      throw e
    }
  }

  onTreeUpdate(params: { event: Record<string, DropContext<TreeData>>; instance: OfficeAllocDraggableTree }): void {
    const firstEvent = this.getFirstEvent(params)
    const { event, instance } = firstEvent

    if (event.added) {
      if (instance.$props.type !== OfficeAllocType.BUSINESS_OFFICE) {
        return
      }

      const targetData = instance.$props.data

      // 営業所階層一覧へ
      // 本社から最下位までは10を超える場合、エラーとする
      this.findTreeDepthWithTarget(this.masterTree, targetData[0])
    }

    this.initTreeCache()
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getFirstEvent(params: { event: Record<string, any>; instance: OfficeAllocDraggableTree }): {
    event: Record<string, DropContext<TreeData>>
    instance: OfficeAllocDraggableTree
  } {
    const { event } = params
    if (!event.event) {
      return params
    }

    return this.getFirstEvent({ event: event.event, instance: event.instance })
  }

  depthErr(): void {
    msgBoxService.info(i18n.t('vaildate.common.overDeep', [this.pageName, 10]).toString())
    this.rollbackToTreeCache()
  }

  findTreeDepthWithTarget(baseData: TreeData[], targetData: TreeData): void {
    const depth = {
      depth: 1
    }

    this.findParentsDepth(baseData, targetData, depth)
    if (depth.depth > 10) {
      this.depthErr()
      return
    }

    const overDeep = this.findChildrenDepth(targetData, depth)
    if (overDeep) {
      this.depthErr()
      return
    }
  }

  findParentsDepth(baseData: TreeData[], targetData: TreeData, depth: Record<string, number>): boolean {
    let got = false

    if (baseData && baseData.length > 0) {
      for (const item of baseData) {
        if (item.id === targetData.id) {
          got = true
          break
        }

        if (item.children && item.children.length > 0) {
          const newDepth = {
            depth: depth.depth + 1
          }
          got = this.findParentsDepth(item.children, targetData, newDepth)

          if (got) {
            depth.depth = newDepth.depth
            break
          }
        }
      }
    }

    return got
  }

  findChildrenDepth(targetData: TreeData, depth: Record<string, number>): boolean {
    let overDeep = false
    if (targetData.children && targetData.children.length > 0) {
      for (const item of targetData.children) {
        const newDepth = {
          depth: depth.depth + 1
        }

        if (this.findChildrenDepth(item, newDepth)) {
          overDeep = true
          break
        }

        if (newDepth.depth > 10) {
          overDeep = true
          break
        }
      }
    }

    return overDeep
  }
}
</script>

<style lang="scss" scoped>
::v-deep .tti-bottom-right-area .tti-btn-default {
  margin-right: 0 !important;
}

.customTree {
  height: inherit;

  > ul {
    padding-left: 0;
    margin-top: 0;
    white-space: nowrap;
  }
}

.tti-tree {
  overflow: auto;

  .tti-content-title {
    font-size: $FS02;
  }

  ::v-deep ul {
    padding-left: 24px;
  }

  ::v-deep .content {
    > ul {
      padding-left: 0;
      margin-top: 0;
      white-space: nowrap;
    }

    span {
      display: inline-block;
      padding: 4px 0;
    }
  }
}
</style>
